import React from "react";
import { useLocalStore, useObserver } from "mobx-react";
import {
  DialogActions,
  Grid,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  makeStyles,
  FormLabel,
  TextareaAutosize,
  FormControl,
  alpha,
  Box
} from "@material-ui/core";
import { Add, Remove, Info } from "@material-ui/icons";
import {
  Date,
  FormxDebugger,
  RadioGroup,
  Text,
  ToggleButtonGroup
} from "@components/formx";
import cn from "classnames";
import { toString } from "lodash";
import { Form } from "@modules/formx-react";
import { PrimaryButton, RegularButton, Spinner, WithTooltip } from "@components";
import { useNavigation } from "@navigation";
import { AccommodationTypeField } from "./AccommodationTypeField";
import {
  AccommodationRequestStore,
  acceptedOveragePercentOptions
} from "./AccommodationRequestStore";
import { AccommodationRequestProps } from "./AccommodationRequestProps";
import { FileUploadField } from "./FileUploadField";
import { WorkRequestPeriods } from "../../components/week-periods";
import { ConsultantSelector } from "@containers/VirtualSelector/ConsultantSelector";
import { ProjectSelector } from "@bookings/ProjectSelector";
import { getOptions } from "@common/CostCenterSelect";
import Select from "@components/Select";
import { Color, NumeralTextField } from "@bonliva-crm/ui";
import { useLocale } from "@hooks";
import { getErrorMessageIfValueExists } from "../utils";
import TicketCategories from "@tickets/TicketCreate/ticket-categories";

export function AccommodationRequestForm(
  props: AccommodationRequestProps & { isNew: boolean }
) {
  const nav = useNavigation();
  const classes = useStyles();
  const { locale } = useLocale();

  const localStore = useLocalStore(
    () => new AccommodationRequestStore(props.bookingGroupId, props, nav)
  );

  const labels = locale.accommodationRequest;

  nav.lockNavigation("hospitality_request", localStore.isFormDirty);

  const renderConsultantAndProjectFields = () => (
    <>
      <Grid item xs={12}>
        <ConsultantSelector
          fullWidth
          required
          disabled={localStore.isFormDisabled}
          name="consultantId"
          label={locale.Consultants}
          onChange={localStore.setConsultant}
          value={localStore.consultantValue}
          error={props.isNew && localStore.consultantError}
        />
      </Grid>
      <Grid item xs={12}>
        <ProjectSelector
          clearable
          ProjectSelectProps={{ disabled: localStore.isFormDisabled, required: true }}
          store={localStore.projectSelectorStore}
          value={localStore.form.values.projectId}
          error={props.isNew && !!localStore.form.getError("projectId")}
          onChange={(v) => localStore.form.setValue("projectId", v)}
        />
      </Grid>
      <Grid item xs={12}>
        <Select
          name="costCenterId"
          fullWidth
          required
          disabled={localStore.isFormDisabled}
          label={locale.CostCenter}
          options={getOptions(localStore.costCenters)}
          value={localStore.form.values.costCenterId}
          onChange={localStore.setCostCenter}
          error={!!localStore.form.getError("costCenterId")}
        />
      </Grid>
    </>
  );

  const renderBasicAccommodationFields = (isBooking: boolean) => (
    <Grid container spacing={2} direction="column" className={classes.mt16}>
      <Grid item xs={12} sm={6} lg={4}>
        <Text
          name="customerAddress"
          label={labels.customerAddress}
          disabled={isBooking || localStore.isFormDisabled}
        />
      </Grid>

      <Grid item xs={12} sm={6} lg={4}>
        <Text
          name="city"
          label={labels.city}
          TextFieldProps={{
            required: true,
            InputProps: {
              startAdornment: (
                <WithTooltip title={labels.cityInfo}>
                  <Info className={classes.infoInputIcon} fontSize="small" />
                </WithTooltip>
              )
            }
          }}
        />
      </Grid>
    </Grid>
  );

  const renderDifferentCityFields = () => (
    <>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={localStore.form.values.differentCity}
              disabled={localStore.isFormDisabled}
              onChange={(e) =>
                localStore.form.setValue("differentCity", e.target.checked)
              }
              color="primary"
            />
          }
          label={labels.differentCity}
        />
      </Grid>

      {localStore.shouldShowDifferentCityField && (
        <Grid item xs={6}>
          <Text
            name="specifyCity"
            label={labels.specifyCity}
            TextFieldProps={{
              required: true,
              disabled: localStore.isFormDisabled
            }}
          />
        </Grid>
      )}
    </>
  );

  const renderBookingPeriodSection = () => (
    <>
      <Grid item xs={12} alignItems={"center"} className={classes.bookingPeriodHeader}>
        <Typography variant="subtitle1" className={classes.bookingPeriodTitle}>
          {localStore.bookingPeriodTitle}
        </Typography>
        <WithTooltip title={labels.periodTooltip}>
          <Info className={classes.infoIcon} fontSize="small" />
        </WithTooltip>
      </Grid>
      <Grid item xs={12}>
        <WorkRequestPeriods
          onChange={(v) => localStore.setWeeks(v)}
          bookings={localStore.bookingWeeks}
          requested={localStore.bookingRequestedWeeks}
          value={localStore.periodsValue}
          isBookingGroupContext={localStore.isBookingGroupContext}
        />
      </Grid>
    </>
  );

  const renderRepeatSection = () => (
    <>
      <Grid item xs={4} className={classes.repeatField}>
        <RadioGroup
          name="repeat"
          options={localStore.repeatOptions}
          label={locale.Repeat}
          noLabelMargin
          row
        />
      </Grid>
      {localStore.shouldShowWeekDaysSection && (
        <Grid item xs={12}>
          <ToggleButtonGroup
            name="weekDays"
            exclusive={false}
            wrap
            label={locale.RepeatOnWeekDaysAccForm}
            helperText={locale.RepeatOnWeekDaysExample}
            options={localStore.weekDaysOptions}
          />
        </Grid>
      )}
    </>
  );

  const renderAccommodationPeriods = () => (
    <Grid container xs={12}>
      <Grid item xs={9}>
        <div className={classes.periodsContainer}>
          {localStore.form.values.periods.map((_, index) => {
            const constraints = localStore.periodDateConstraints.find(
              (c) => c.index === index
            );

            return (
              <div key={index} className={classes.periodRow}>
                <div>
                  <Date
                    name={`periods.${index}.start`}
                    label={locale.travelRequest.checkIn}
                    DatePickerProps={{
                      required: true,
                      minDate: constraints?.minStart
                    }}
                  />
                </div>

                <div className={classes.periodSeparator}>
                  <span className={classes.periodDash}>&mdash;</span>
                </div>

                <div>
                  <Date
                    name={`periods.${index}.end`}
                    label={locale.travelRequest.checkOut}
                    DatePickerProps={{
                      required: true,
                      minDate: constraints?.maxEnd
                    }}
                  />
                </div>

                <div>
                  <IconButton
                    disabled={!localStore.canAddPeriod}
                    onClick={() => localStore.addPeriod(index)}
                    className={classes.periodButton}
                  >
                    <Add />
                  </IconButton>

                  <IconButton
                    disabled={!localStore.canRemovePeriod}
                    onClick={() => localStore.removePeriod(index)}
                    className={classes.periodButton}
                  >
                    <Remove />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </div>
      </Grid>
    </Grid>
  );

  const renderAccommodationSuggestionSection = () => {
    const isBookingInformationError = Boolean(
      localStore.form.getError("bookingInformation")
    );

    return (
      <Grid item xs={12} sm={8} className={classes.accommodationSuggestionSection}>
        <FormControlLabel
          control={
            <Checkbox
              checked={localStore.form.values.hasAccommodationSuggestion}
              onChange={(e) => localStore.setHasAccommodationSuggestion(e.target.checked)}
              color="primary"
              disabled={localStore.isFormDisabled}
            />
          }
          label={labels.accommodationSuggestion}
        />
        <div className={classes.accommodationSuggestionDescription}>
          <Typography variant="subtitle2" color="textSecondary">
            {labels.accommodationSuggestionSubtitle}
          </Typography>
          {labels.accommodationSuggestionDescription.map((description, index) => (
            <Typography
              key={index}
              variant="body2"
              color="textSecondary"
              className={classes.mt8}
            >
              {description}
            </Typography>
          ))}

          {localStore.shouldShowAccommodationSuggestionSection && (
            <Grid
              container
              spacing={1}
              className={classes.accommodationSuggestionContent}
            >
              <Grid item xs={12}>
                <RadioGroup
                  name="staffAccommodation"
                  options={locale.yesNoOptions}
                  label={labels.staffAccommodation}
                  LabelProps={{ required: true, disabled: localStore.isFormDisabled }}
                  FormControlLabelProps={{
                    classes: {
                      label: classes.staffAccommodationLabel
                    }
                  }}
                  row
                  noLabelMargin
                />
              </Grid>

              {localStore.shouldShowFileUploadSection && (
                <Grid item xs={12}>
                  <Typography variant="body1" gutterBottom>
                    {labels.bookingConfirmationFile}
                  </Typography>
                  <FileUploadField
                    value={localStore.form.values.bookingConfirmationFile}
                    onChange={localStore.setBookingConfirmationFile}
                    disabled={localStore.isFormDisabled}
                    label={labels.bookingConfirmationFile}
                    error={localStore.form.getError("bookingConfirmationFile")}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={7}>
                <Text
                  name="accommodationLink"
                  label={labels.accommodationLink}
                  TextFieldProps={{ placeholder: "https://", type: "url" }}
                  disabled={localStore.isFormDisabled}
                />
              </Grid>

              <Grid item xs={7} className={classes.bookingInformationWrapper}>
                <FormLabel required error={isBookingInformationError}>
                  {labels.bookingInformation}
                </FormLabel>
                <Typography variant="body2" color="textSecondary">
                  {labels.bookingInformationSubtitle}
                </Typography>
                <TextareaAutosize
                  name="bookingInformation"
                  className={cn(
                    classes.bookingInformationField,
                    isBookingInformationError && classes.errorInput
                  )}
                  disabled={localStore.isFormDisabled}
                  required
                  minRows={4}
                  value={localStore.form.values.bookingInformation}
                  onChange={(e) =>
                    localStore.form.setValue("bookingInformation", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          )}
        </div>
      </Grid>
    );
  };

  const renderCarAccessField = () => {
    const parkingMaxPriceError = localStore.form.getError("parkingMaxPrice");
    const errorMessage = getErrorMessageIfValueExists(
      parkingMaxPriceError,
      localStore.form.values.parkingMaxPrice
    );

    return (
      <>
        {localStore.shouldShowLegacyCarAccess && (
          <Grid item xs={12}>
            <Text
              name="carAccess"
              label={labels.carAccess}
              disabled={localStore.isFormDisabled}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <RadioGroup
            name="hasOwnCar"
            options={locale.yesNoOptions}
            label={labels.hasOwnCar}
            disabled={localStore.isFormDisabled}
            LabelProps={{ required: true }}
            row
            noLabelMargin
          />
        </Grid>
        {localStore.shouldShowHasCarAccessField && (
          <Grid item xs={12} className={classes.mt8}>
            <RadioGroup
              name="hasCarAccess"
              options={locale.yesNoOptions}
              label={labels.hasCarAccess}
              disabled={localStore.isFormDisabled}
              LabelProps={{ required: true }}
              row
              noLabelMargin
            />
          </Grid>
        )}
        {localStore.shouldShowParkingMaxPriceField && (
          <Grid item xs={12}>
            <NumeralTextField
              className={classes.parkingMaxPriceInput}
              name="parkingMaxPrice"
              label={labels.parkingMaxPrice}
              disabled={localStore.isFormDisabled}
              required
              error={!!parkingMaxPriceError}
              helperText={errorMessage}
              value={localStore.form.values.parkingMaxPrice}
              suffix="kr"
              format="0,0"
              allowDecimals={false}
              onChange={(_, numericValue) => {
                localStore.form.setValue("parkingMaxPrice", toString(numericValue));
              }}
            />
          </Grid>
        )}
      </>
    );
  };

  const renderBudgetAndAcceptedDifference = () => {
    const budgetError = localStore.form.getError("budget");
    const budgetPeriodError = localStore.form.getError("budgetPeriod");
    const acceptedOveragePercentError = localStore.form.getError(
      "acceptedOveragePercent"
    );
    const errorMessage = getErrorMessageIfValueExists(
      budgetError,
      localStore.form.values.budget
    );

    return (
      <Grid item container className={classes.mt24} spacing={1}>
        <Grid item xs={12}>
          <RadioGroup
            name="budgetPeriod"
            options={localStore.budgetPeriodOptions}
            label={labels.budgetPeriod}
            LabelProps={{ required: true, error: !!budgetPeriodError }}
            disabled={localStore.isFormDisabled}
            row
            noLabelMargin
          />
        </Grid>
        <Grid className={classes.budgetSection} item xs={1}>
          <FormControl error={!!budgetError}>
            <FormLabel required>
              {labels.getBudgetLabel(localStore.form.values.budgetPeriod)}
            </FormLabel>
            <Typography variant="body2" color="error">
              {errorMessage}&nbsp;
            </Typography>
            <NumeralTextField
              key={localStore.sameAsLastTime + "budget"}
              name="budget"
              disabled={localStore.isFormDisabled}
              required
              className={classes.budgetInput}
              error={!!budgetError}
              value={localStore.form.values.budget}
              suffix="kr"
              format="0,0"
              allowDecimals={false}
              onChange={(_, numericValue) => {
                localStore.form.setValue("budget", toString(numericValue));
              }}
              InputProps={{
                startAdornment: (
                  <WithTooltip title={labels.budgetTooltip}>
                    <Info className={classes.inputIcon} fontSize="small" />
                  </WithTooltip>
                )
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl>
            <FormLabel required error={!!acceptedOveragePercentError}>
              {labels.acceptedOveragePercent}
            </FormLabel>
            <Typography variant="body2" color="textSecondary">
              {labels.acceptedOveragePercentInfo}
            </Typography>
            <Select
              name="acceptedOveragePercent"
              fullWidth
              placeholder="- Välj procent -"
              clearable={false}
              error={!!acceptedOveragePercentError}
              disabled={localStore.isFormDisabled}
              options={acceptedOveragePercentOptions}
              className={classes.acceptedDifferenceSelect}
              value={localStore.form.values.acceptedOveragePercent}
              onChange={(v) => localStore.form.setValue("acceptedOveragePercent", v)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          {localStore.shouldShowPriceInterval && (
            <div className={classes.priceIntervalBox}>
              <Typography variant="body2" className={classes.priceIntervalTitle}>
                Accepterat prisintervall:
              </Typography>
              <Typography variant="body1">{localStore.priceRangeDisplayValue}</Typography>
            </div>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderActionButtons = () => (
    <DialogActions className={classes.dialogActions}>
      <RegularButton className={classes.cancelButton} onClick={() => nav.close()}>
        {locale.Cancel}
      </RegularButton>

      <PrimaryButton
        disabled={!localStore.canSubmitForm}
        loading={localStore.isFormSubmitting}
        onClick={localStore.form.submit}
      >
        {locale.Send}
      </PrimaryButton>
    </DialogActions>
  );

  const renderWalkingDistanceField = () => {
    const error = localStore.form.getError("walkingDistance");
    const errorMessage = getErrorMessageIfValueExists(
      error,
      localStore.form.values.walkingDistance
    );

    return (
      <NumeralTextField
        key={localStore.sameAsLastTime + "walkingDistance"}
        name="walkingDistance"
        label={labels.walkingDistance}
        fullWidth
        // required
        className={classes.walkingDistanceInput}
        disabled={localStore.isFormDisabled}
        suffix="meter"
        format="0,0"
        allowDecimals={false}
        value={localStore.form.values.walkingDistance}
        error={props.isNew && !!error}
        helperText={errorMessage}
        onChange={(_, numericValue) => {
          localStore.form.setValue("walkingDistance", toString(numericValue));
        }}
      />
    );
  };

  return useObserver(() => {
    if (localStore.loading) {
      return <Spinner />;
    }

    return (
      <div className={classes.accommodationRequestForm}>
        <Form store={localStore.form} allowUpdates>
          <FormxDebugger store={localStore.form} />

          <Grid container spacing={0} xs={12}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localStore.sameAsLastTime}
                    onChange={() => localStore.handleSameAsLastTime()}
                    color="primary"
                    disabled={
                      localStore.isFormDisabled || !localStore.lastAccommodationRequest
                    }
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    {labels.sameAsLastTime}
                    <WithTooltip title={labels.sameAsLastTimeInfo}>
                      <Info className={classes.infoIcon} fontSize="small" />
                    </WithTooltip>
                  </Box>
                }
              />
            </Grid>

            {localStore.shouldShowProlongation && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={localStore.form.values.isProlongation}
                      onChange={(e) => localStore.setIsProlongation(e.target.checked)}
                      disabled={localStore.isFormDisabled}
                    />
                  }
                  label={labels.isProlongation}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={7} lg={5} container>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={localStore.form.values.isReschedule}
                      onChange={(e) => localStore.setIsReschedule(e.target.checked)}
                      disabled={localStore.isFormDisabled}
                    />
                  }
                  label={labels.isReschedule}
                />
              </Grid>

              {localStore.form.values.isReschedule && (
                <Grid item xs={12}>
                  <Text
                    name="rescheduleComment"
                    label={labels.rescheduleComment}
                    TextFieldProps={{
                      required: true,
                      disabled: localStore.isFormDisabled
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>

          <div className={classes.accommodationTypeField}>
            <AccommodationTypeField name="accommodationType" />
          </div>

          <Grid container spacing={1}>
            {!localStore.isBookingGroupContext && renderConsultantAndProjectFields()}

            {/* Basic accommodation information */}
            {renderBasicAccommodationFields(localStore.isBookingContext)}
            {renderDifferentCityFields()}

            {/* Booking period and repeat options */}
            <Grid container xs={12} spacing={1}>
              {localStore.showBookingPeriod && renderBookingPeriodSection()}
              {localStore.shouldShowRepeatSection && renderRepeatSection()}
            </Grid>

            {/* Accommodation periods and dates */}
            {renderAccommodationPeriods()}

            {/* Accommodation suggestion section */}
            {renderAccommodationSuggestionSection()}

            <Grid container spacing={2}>
              {/* Budget and payment with accepted difference */}
              {renderBudgetAndAcceptedDifference()}

              <Grid item xs={12} className={classes.mt16}>
                <RadioGroup
                  name="paidBy"
                  noLabelMargin
                  options={localStore.paidByOptions}
                  disabled={localStore.isFormDisabled}
                  label={labels.paidBy}
                  LabelProps={{ required: true }}
                  row
                />
              </Grid>

              {/* Comment about booking */}
              <Grid item xs={12} sm={7} lg={5}>
                <Text
                  name="periodInput"
                  label={labels.commentAboutBooking}
                  TextFieldProps={{
                    multiline: true,
                    disabled: localStore.isFormDisabled
                  }}
                />
              </Grid>

              {/* Car access */}
              <Grid item container xs={12} className={classes.mt16}>
                {renderCarAccessField()}
              </Grid>

              {/* Walking distance */}
              <Grid item xs={6}>
                {renderWalkingDistanceField()}
              </Grid>

              {/* Shared conveniences */}
              {localStore.shouldShowLegacySharedConveniences && (
                <Grid item xs={12}>
                  <Text
                    name="sharedConveniences"
                    label={labels.sharedConveniences}
                    TextFieldProps={{ disabled: localStore.isFormDisabled }}
                  />
                </Grid>
              )}

              {/* Additional preferences with shared conveniences */}
              <Grid item xs={12}>
                <Grid item xs={3} container direction="column">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localStore.form.values.sharedKitchen}
                        onChange={(e) =>
                          localStore.form.setValue("sharedKitchen", e.target.checked)
                        }
                        color="primary"
                        disabled={localStore.isFormDisabled}
                      />
                    }
                    label={labels.sharedKitchen}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localStore.form.values.sharedBathroom}
                        onChange={(e) =>
                          localStore.form.setValue("sharedBathroom", e.target.checked)
                        }
                        color="primary"
                        disabled={localStore.isFormDisabled}
                      />
                    }
                    label={labels.sharedBathroom}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} container>
                <Grid item xs={12} sm={7} lg={5}>
                  <FormControl>
                    <FormLabel required error={!!localStore.ticketDifficultyLevelError}>
                      {locale.ticketDifficultyLevel.title}
                    </FormLabel>
                    <Typography variant="body2" color="textSecondary">
                      {locale.ticketDifficultyLevel.subtitle}
                    </Typography>

                    <Select
                      name="ticketDifficultyLevel"
                      required
                      fullWidth
                      clearable={false}
                      className={classes.ticketDifficultyLevelSelect}
                      disabled={localStore.isFormDisabled}
                      options={localStore.ticketDifficultyLevelOptions}
                      value={localStore.form.values.ticketDifficultyLevel}
                      onChange={(value) => localStore.setTicketDifficultyLevel(value)}
                      error={!!localStore.ticketDifficultyLevelError}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <TicketCategories
                  selected={localStore.form.values.ticketDifficultyLevel}
                />
              </Grid>

              {/* Additional info */}
              <Grid item xs={12} sm={7} lg={5}>
                <Text
                  name="accommodationPreferences"
                  label={labels.additionalInfo}
                  helperText={labels.additionInfoDesc}
                  TextFieldProps={{
                    multiline: true,
                    disabled: localStore.isFormDisabled
                  }}
                />
              </Grid>

              {/* ZZ number */}
              {localStore.isGotlandRegionOrg && (
                <Grid item xs={12}>
                  <Text
                    name="zzNumber"
                    label={labels.zzNumber}
                    disabled={localStore.isFormDisabled}
                  />
                </Grid>
              )}

              {/* Accommodation suggestion */}
              <Grid className={classes.accommodationTypeSection} item xs={12}>
                <RadioGroup
                  name="accommodationSuggestion"
                  row
                  noLabelMargin
                  LabelProps={{ required: true }}
                  label={labels.shouldBeBookedDirectly}
                  options={localStore.accommodationTypeOptions}
                  disabled={localStore.isFormDisabled}
                />
              </Grid>
            </Grid>
          </Grid>

          {localStore.shouldShowActionButtons && renderActionButtons()}
        </Form>
      </div>
    );
  });
}

const useStyles = makeStyles((theme) => ({
  accommodationRequestForm: {
    "& label.Mui-required": {
      color: `${alpha(theme.palette.common.black, 0.54)} !important`
    },
    "& label.Mui-required > span": {
      color: `${theme.palette.error.main} !important`
    }
  },
  accommodationTypeField: {
    margin: "8px 0"
  },
  bookingPeriodHeader: {
    display: "flex",
    marginTop: 20
  },
  bookingPeriodTitle: {
    display: "inline-block",
    color: alpha(theme.palette.common.black, 0.54)
  },
  infoIcon: {
    marginLeft: 5,
    color: "gray",
    cursor: "help"
  },
  infoInputIcon: {
    marginRight: 5,
    color: "gray",
    cursor: "help"
  },
  repeatField: {
    marginTop: 20
  },
  periodsContainer: {
    width: "100%"
  },
  periodRow: {
    display: "flex",
    alignItems: "center"
  },
  periodSeparator: {
    padding: 20
  },
  periodDash: {
    fontWeight: "bold",
    position: "relative",
    top: 10
  },
  periodButton: {
    position: "relative",
    top: 7
  },
  accommodationSuggestionSection: {
    marginTop: 24
  },
  accommodationSuggestionDescription: {
    marginLeft: 32,
    marginBottom: 8
  },
  accommodationSuggestionContent: {
    marginTop: 8
  },
  budgetSection: {
    marginRight: 24,
    minWidth: 180
  },
  budgetInput: {
    textAlign: "center"
  },
  acceptedDifferenceSection: {},
  inputIcon: {
    marginRight: 5,
    color: "gray",
    cursor: "help"
  },

  accommodationTypeSection: {
    marginTop: 8
  },
  dialogActions: {
    padding: "16px 0"
  },
  cancelButton: {
    marginLeft: "auto"
  },
  bookingInformationWrapper: {
    marginTop: 8
  },
  bookingInformationField: {
    marginTop: 8,
    width: "100%",
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body1.fontSize
  },
  staffAccommodationLabel: {
    marginLeft: -5
  },
  acceptedDifferenceSelect: {
    width: 130
  },
  priceIntervalBox: {
    backgroundColor: Color.LightOrange,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "12px 36px",
    borderRadius: "4px",
    marginTop: 35,
    minWidth: 200,
    maxWidth: 400,
    width: "max-content"
  },
  priceIntervalTitle: {
    marginBottom: "4px",
    fontWeight: 500
  },
  errorInput: {
    borderColor: theme.palette.error.main
  },
  walkingDistanceInput: {
    width: 360
  },
  parkingMaxPriceInput: {
    width: 330
  },
  ticketDifficultyLevelSelect: {
    width: 200
  },
  mt16: {
    marginTop: 16
  },
  mt24: {
    marginTop: 24
  },
  mt8: {
    marginTop: 8
  }
}));
